import { useExternalBookingSettings } from "api/api-hooks/use-external-booking-settings";
import { Badge, Icons, Stack, ThemeSpaceVariable } from "@caspeco/casper-ui-library";
import { AvailabilityBadge } from "components/availability-badge";
import { AvailabilityState, hasAvailabilityBadge } from "utils/availability-utils";
import {
    ActivityBadgeSection,
    getFormattedActivityLength,
    getFormattedActivityPrice,
    getFormattedGroupSizeRange,
    getPaymentBadgeTranslationKey,
    getSectionBookingDurations,
} from "components/selection-components/select-activity/activity-badges/activity-badges-helper";
import { useTranslation } from "react-i18next";

interface IActivityBadges {
    sections: ActivityBadgeSection[];
    maximumGapTime?: number;
    inModal?: boolean;
    hasMenuChoice?: boolean;
    availabilityState?: AvailabilityState | null;
}

function ActivityBadges({
    sections,
    inModal,
    hasMenuChoice,
    maximumGapTime,
    availabilityState,
}: IActivityBadges) {
    const { t } = useTranslation();
    const { data: settings } = useExternalBookingSettings();
    const hideEndTime = !!settings?.hideEndTimeForGuest;
    const sittingLengths = getSectionBookingDurations(sections, maximumGapTime);
    const formattedSittingLengths = getFormattedActivityLength(sittingLengths, t);
    const formattedActivityPrice = getFormattedActivityPrice(sections, t);
    const formattedGroupSizeRange = getFormattedGroupSizeRange(sections, t);
    const paymentBadgeTranslationKey = getPaymentBadgeTranslationKey(sections);

    const hasLengthBadge = sittingLengths.length > 0 && !hideEndTime;
    if (
        !hasLengthBadge &&
        !paymentBadgeTranslationKey &&
        !formattedActivityPrice &&
        !formattedGroupSizeRange &&
        !hasMenuChoice &&
        !hasAvailabilityBadge(availabilityState)
    )
        return null;

    return (
        <Stack
            direction="row"
            py={inModal ? ThemeSpaceVariable.Small : ThemeSpaceVariable.None}
            flexWrap="wrap"
        >
            <AvailabilityBadge state={availabilityState} />
            {hasLengthBadge && <Badge.Primary>{formattedSittingLengths}</Badge.Primary>}
            {paymentBadgeTranslationKey && (
                <Badge.Primary>{t(paymentBadgeTranslationKey)}</Badge.Primary>
            )}
            {formattedActivityPrice && <Badge.Primary>{formattedActivityPrice}</Badge.Primary>}
            {formattedGroupSizeRange && <Badge.Primary>{formattedGroupSizeRange}</Badge.Primary>}
            {hasMenuChoice && (
                <Badge.Primary icon={Icons.ModuleMenus}>
                    {t("experienceView.menuChoice")}
                </Badge.Primary>
            )}
        </Stack>
    );
}

export default ActivityBadges;
